import { useTranslation } from "@/hooks";
import {
	PaymentAddDialogStatus,
	PaymentAddDialogUsdtInfo,
	PaymentDetailsDialog,
} from "@/pages/payment/shared";
import { useActOnPayment } from "@/services";
import { formatNumber } from "@/utils/tools";
import {
	ActionItem,
	ActionMenu,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	toast,
} from "@st-marketplace/ui";
import { useState } from "react";

import type { PaymentRow } from "../../../../../types";

interface PaymentSentTableActionProps {
	payment: PaymentRow;
}

const MANUAL_PAYMENT_TYPES = ["MARKET_MANUAL", "SYSTEM_MANUAL"];
/** The two payment shapes that carry a wallet to transfer to. */
const isAutoPayment = (
	payment: PaymentRow,
): payment is Extract<
	PaymentRow,
	{ type: "MARKET_AUTOMATIC" | "SYSTEM_AUTOMATIC" }
> => payment.type === "MARKET_AUTOMATIC" || payment.type === "SYSTEM_AUTOMATIC";

function PaymentSentTableAction({ payment }: PaymentSentTableActionProps) {
	const { t } = useTranslation();

	const canRejectPayment =
		payment.state === "pending" &&
		payment.type &&
		MANUAL_PAYMENT_TYPES.includes(payment.type);

	const actOnPayment = useActOnPayment();

	const [menuActionOpen, setMenuActionOpen] = useState<boolean>(false);
	const [rejectDialog, setRejectDialog] = useState<boolean>(false);
	const handleRejectPayment = () => {
		actOnPayment.mutate(
			{
				path: { payment_id: payment.id },
				body: {
					type_:
						payment.type === "MARKET_MANUAL"
							? "MARKET_PAYMENT_ACTION"
							: "SYSTEM_PAYMENT_ACTION",
					action: "reject",
				},
			},
			{
				onSuccess: () => {
					setRejectDialog(false);
					setMenuActionOpen(false);
					toast.success(t("payment.action.cancelSuccess"));
				},
			},
		);
	};

	const [detailsDialog, setDetailsDialog] = useState<boolean>(false);

	/**
	 * A USDT payment isn't finished when the dialog closes - the transfer still
	 * has to be made and checked. Let the row reopen that same wallet dialog.
	 */
	const isAwaitingTransfer =
		payment.state === "pending" && isAutoPayment(payment);
	const [walletDialog, setWalletDialog] = useState<boolean>(false);
	const [confirmedDialog, setConfirmedDialog] = useState<boolean>(false);

	return (
		<>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-only container; inner menu remains keyboard-accessible */}
			<div
				className="flex items-center justify-end gap-1"
				onClick={(event) => event.stopPropagation()}
			>
				<ActionMenu open={menuActionOpen} onOpenChange={setMenuActionOpen}>
					{isAwaitingTransfer && (
						<ActionItem
							leadingIconName="exchange-line"
							onClick={() => {
								setMenuActionOpen(false);
								setWalletDialog(true);
							}}
						>
							{t("payment.action.continueTransfer")}
						</ActionItem>
					)}
					{canRejectPayment && (
						<ActionItem
							leadingIconName="close-line"
							loading={actOnPayment.isPending}
							onClick={() => {
								setMenuActionOpen(false);
								setRejectDialog(true);
							}}
						>
							{t("action.cancel")}
						</ActionItem>
					)}
					<ActionItem
						leadingIconName="information-line"
						onClick={() => {
							setMenuActionOpen(false);
							setDetailsDialog(true);
						}}
					>
						{t("action.details")}
					</ActionItem>
				</ActionMenu>
			</div>

			<Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
						<DialogDescription>
							{t("payment.action.cancelMessage")}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.no"),
								props: {
									type: "button",
									onClick: () => setRejectDialog(false),
								},
							},
							primary: {
								label: t("action.button.confirmCancel"),
								props: {
									type: "button",
									loading: actOnPayment.isPending,
									onClick: handleRejectPayment,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>

			{walletDialog && isAutoPayment(payment) && (
				<PaymentAddDialogUsdtInfo
					data={payment}
					open={walletDialog}
					onOpenChange={setWalletDialog}
					onSuccess={() => {
						setWalletDialog(false);
						setConfirmedDialog(true);
					}}
				/>
			)}

			<PaymentAddDialogStatus
				method="accepted"
				open={confirmedDialog}
				onOpenChange={setConfirmedDialog}
				amount={
					payment.amount ? `${formatNumber(payment.amount, "price")}` : "0"
				}
				user={
					"seller_name" in payment && payment.seller_name
						? payment.seller_name
						: ""
				}
			/>

			<PaymentDetailsDialog
				payment={payment}
				open={detailsDialog}
				onOpenChange={setDetailsDialog}
			/>
		</>
	);
}

export default PaymentSentTableAction;
